import axios, {
  AxiosRequestConfig,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import merge from './utils';

// probably we are going to need following parameters
export type HttpRequestConfig<D = any> = Pick<
  AxiosRequestConfig<D>,
  'baseURL' | 'url' | 'params' | 'data' | 'withCredentials' | 'headers' | 'responseType' | 'paramsSerializer'
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type HttpResponse<T = any, D = any> = AxiosResponse;

export default class HttpClient {
  protected axios: AxiosInstance;

  readonly defaultContentType = 'application/json';

  constructor(config?: HttpRequestConfig) {
    this.axios = axios.create(config);
  }

  get<T = any, R = HttpResponse<T>, D = any>(url: string, config?: HttpRequestConfig<D>): Promise<R> {
    return this.axios.get<T, R, D>(url, config);
  }

  post<T = any, R = HttpResponse<T>, D = any>(url: string, data?: D, config?: HttpRequestConfig<D>): Promise<R> {
    return this.axios.post<T, R, D>(url, data, this.setDefaultContentType(config));
  }

  put<T = any, R = HttpResponse<T>, D = any>(url: string, data?: D, config?: HttpRequestConfig<D>): Promise<R> {
    return this.axios.put<T, R, D>(url, data, this.setDefaultContentType(config));
  }

  delete<T = any, R = HttpResponse<T>, D = any>(url: string, config?: HttpRequestConfig<D>): Promise<R> {
    return this.axios.delete<T, R, D>(url, config);
  }

  // eslint-disable-next-line class-methods-use-this
  isHttpError(error: any): boolean {
    return isAxiosError(error);
  }

  onRequest(
    onFulfilled: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: any) => any,
  ) {
    return this.axios.interceptors.request.use(onFulfilled, onRejected);
  }

  onResponse(
    onFulfilled: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any,
  ) {
    return this.axios.interceptors.response.use(onFulfilled, onRejected);
  }

  removeRequestInterceptor(id: number) {
    return this.axios.interceptors.request.eject(id);
  }

  removeResponseInterceptor(id: number) {
    return this.axios.interceptors.response.eject(id);
  }

  private setDefaultContentType<D = any>(config?: HttpRequestConfig<D>) {
    return merge({ headers: { 'Content-Type': this.defaultContentType } }, config ?? {});
  }
}
